import React, { useState, useEffect } from 'react';
import { useCart } from '../contexts/CartContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Building2, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function CheckoutPage() {
  const { cart, clearCart } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paypalConfig, setPaypalConfig] = useState({ enabled: false, paypal_email: '', paypal_me_link: '' });
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const raw = localStorage.getItem('applied_coupon');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    city: '',
    postal_code: '',
    notes: ''
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/paypal/config`).then(({ data }) => {
      setPaypalConfig(data);
    }).catch(() => setPaypalConfig({ enabled: false }));
  }, []);

  const freeShippingThreshold = config?.free_shipping_threshold || 50;
  const shippingCost = config?.shipping_cost || 3.90;
  const baseShipping = cart.total >= freeShippingThreshold ? 0 : shippingCost;
  const shipping = appliedCoupon?.free_shipping ? 0 : baseShipping;
  const discount = appliedCoupon?.discount || 0;
  const totalWithShipping = Math.max(cart.total + shipping - discount, 0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cart.items || cart.items.length === 0) {
      toast.error('Tu carrito está vacío');
      return;
    }

    setLoading(true);

    try {
      const items = cart.items.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        customization: item.customization || {}
      }));

      const { data } = await axios.post(`${API_URL}/api/orders/guest`, {
        items,
        ...formData,
        payment_method: paymentMethod,
        coupon_code: appliedCoupon?.code || null
      });

      setOrderComplete(data);
      localStorage.removeItem('applied_coupon');
      setAppliedCoupon(null);
      await clearCart();
      toast.success('¡Pedido realizado con éxito!');
    } catch (error) {
      console.error('Order error:', error);
      toast.error('Error al procesar el pedido. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (orderComplete) {
    const isPaypal = orderComplete.payment_method === 'paypal_manual';
    const orderCode = orderComplete.order_id.substring(0, 8).toUpperCase();
    return (
      <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-10 sm:p-14 text-center" data-testid="order-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h1 className="text-5xl sm:text-6xl font-light tracking-tight text-[#374963] mb-3">
              ¡GRACIAS!
            </h1>
            <p className="text-[#73706e] mb-10">
              Tu pedido ha sido procesado correctamente
            </p>

            <div className="border-t border-b border-[rgba(55,73,99,0.1)] py-6 mb-10">
              <p className="text-xs uppercase tracking-widest text-[#73706e] mb-2">Importe total</p>
              <p className="text-4xl font-semibold text-[#374963]" data-testid="order-success-total">
                {orderComplete.total.toFixed(2)} €
              </p>
            </div>

            {isPaypal ? (
              <div className="text-left space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#73706e] mb-2 text-center">
                    Envía el pago por PayPal a
                  </p>
                  <p className="text-lg font-mono font-semibold text-[#374963] text-center break-all" data-testid="paypal-email-display">
                    {orderComplete.paypal_email}
                  </p>
                  {orderComplete.paypal_me_link && (
                    <div className="text-center mt-3">
                      <a
                        href={orderComplete.paypal_me_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-5 py-2 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors text-sm font-medium"
                      >
                        Pagar en PayPal →
                      </a>
                    </div>
                  )}
                </div>

                <div className="bg-[#F5F1EC] rounded-lg p-5 text-center">
                  <p className="text-sm text-[#73706e] mb-1">
                    En el concepto del pago has de poner<br />el <strong>código de pedido</strong>:
                  </p>
                  <p className="text-2xl font-mono font-bold text-[#374963] tracking-widest mt-2" data-testid="order-success-code">
                    {orderCode}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-left space-y-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#73706e] mb-3 text-center">
                    Cuenta bancaria<br />para el ingreso
                  </p>
                  <p className="text-xl sm:text-2xl font-mono font-semibold text-[#374963] text-center tracking-wider break-all" data-testid="order-success-iban">
                    {orderComplete.bank_iban}
                  </p>
                  {orderComplete.bank_holder && (
                    <p className="text-xs text-[#73706e] text-center mt-2">
                      Titular: {orderComplete.bank_holder}
                    </p>
                  )}
                </div>

                <div className="bg-[#F5F1EC] rounded-lg p-5 text-center">
                  <p className="text-sm text-[#73706e] mb-1">
                    En la descripción de la transferencia<br />has de poner el <strong>código de pedido</strong>:
                  </p>
                  <p className="text-2xl font-mono font-bold text-[#374963] tracking-widest mt-2" data-testid="order-success-code">
                    {orderCode}
                  </p>
                </div>
              </div>
            )}

            <p className="text-sm text-[#73706e] mt-10 mb-6">
              Te hemos enviado un email con los datos del pedido y la información de pago.
            </p>

            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors"
              data-testid="back-to-home-button"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-light text-[#374963] mb-12" data-testid="checkout-page-title">
          Finalizar Compra
        </h1>

        {(!cart.items || cart.items.length === 0) ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <p className="text-[#73706e]">Tu carrito está vacío</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="checkout-form">
            {/* Customer Data */}
            <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6 space-y-6">
              <h2 className="text-2xl font-semibold text-[#374963]">Datos de envío</h2>

              <div>
                <label className="block text-sm font-medium text-[#374963] mb-2">Nombre completo *</label>
                <input type="text" name="customer_name" value={formData.customer_name} onChange={handleChange} required className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-name-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Email *</label>
                  <input type="email" name="customer_email" value={formData.customer_email} onChange={handleChange} required className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Teléfono</label>
                  <input type="tel" name="customer_phone" value={formData.customer_phone} onChange={handleChange} className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-phone-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374963] mb-2">Dirección *</label>
                <input type="text" name="shipping_address" value={formData.shipping_address} onChange={handleChange} required placeholder="Calle, número, piso" className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-address-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Código Postal *</label>
                  <input type="text" name="postal_code" value={formData.postal_code} onChange={handleChange} required className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-postal-code-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374963] mb-2">Ciudad *</label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} required className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-city-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#374963] mb-2">Notas del pedido (opcional)</label>
                <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} className="w-full px-4 py-3 border border-[rgba(55,73,99,0.1)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374963]" data-testid="checkout-notes-input" />
              </div>
            </div>

            {/* Order Summary + Payment */}
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6">
                <h2 className="text-2xl font-semibold text-[#374963] mb-6">Resumen del pedido</h2>
                <div className="space-y-3 mb-4">
                  {cart.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between" data-testid={`checkout-item-${item.product_id}`}>
                      <span className="text-[#73706e]">{item.name} x {item.quantity}</span>
                      <span className="text-[#374963] font-semibold">{(item.price * item.quantity).toFixed(2)}€</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[rgba(55,73,99,0.1)] pt-4 space-y-2">
                  <div className="flex justify-between text-[#73706e]">
                    <span>Subtotal:</span>
                    <span>{cart.total.toFixed(2)}€</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600 font-medium">
                      <span>Descuento ({appliedCoupon.code}):</span>
                      <span>-{discount.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[#73706e]">
                    <span>Portes:</span>
                    <span>{shipping === 0 ? <span className="text-green-600 font-semibold">GRATIS</span> : `${shipping.toFixed(2)}€`}</span>
                  </div>
                  <div className="flex justify-between text-xl font-semibold text-[#374963] pt-2 border-t border-[rgba(55,73,99,0.1)]">
                    <span>Total:</span>
                    <span data-testid="checkout-total">{totalWithShipping.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6 space-y-4">
                <h2 className="text-2xl font-semibold text-[#374963]">Método de pago</h2>

                <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${paymentMethod === 'bank_transfer' ? 'border-[#785534] bg-[#FAFAFA]' : 'border-[rgba(55,73,99,0.1)] hover:border-[#785534]'}`}>
                  <input type="radio" name="payment" value="bank_transfer" checked={paymentMethod === 'bank_transfer'} onChange={(e) => setPaymentMethod(e.target.value)} className="sr-only" data-testid="payment-method-bank-transfer" />
                  <div className="flex items-center space-x-3">
                    <Building2 className="w-6 h-6 text-[#785534]" />
                    <div>
                      <p className="font-semibold text-[#374963]">Transferencia Bancaria</p>
                      <p className="text-sm text-[#73706e]">Te daremos el IBAN al finalizar el pedido</p>
                    </div>
                  </div>
                </label>

                {paypalConfig.enabled && (
                  <label className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${paymentMethod === 'paypal_manual' ? 'border-[#785534] bg-[#FAFAFA]' : 'border-[rgba(55,73,99,0.1)] hover:border-[#785534]'}`}>
                    <input type="radio" name="payment" value="paypal_manual" checked={paymentMethod === 'paypal_manual'} onChange={(e) => setPaymentMethod(e.target.value)} className="sr-only" data-testid="payment-method-paypal" />
                    <div className="flex items-center space-x-3">
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none"><path fill="#003087" d="M20.067 8.478c.492.315.844.825.968 1.478.124.653.03 1.377-.24 2.09-.612 1.62-2.185 2.756-4.243 3.096-1.156.19-2.363.106-3.535.106h-.85l-.542 3.44a.508.508 0 01-.5.428H7.98l1.34-8.5h4.9c1.55 0 3.078.25 4.15.756.71.336 1.32.782 1.697 1.106z"/><path fill="#0070E0" d="M18.5 7.5c-.267 1.72-1.437 3.06-3.076 3.83-.816.39-1.767.552-2.755.552H10.16l-.6 3.798a.6.6 0 01-.6.514H6.4a.508.508 0 01-.5-.605l1.945-12.34a.6.6 0 01.6-.514h5.4c1.9 0 3.6.55 4.4 1.94.4.7.4 1.55.255 2.825z"/></svg>
                      <div>
                        <p className="font-semibold text-[#374963]">PayPal</p>
                        <p className="text-sm text-[#73706e]">Envía el pago a nuestra cuenta PayPal</p>
                      </div>
                    </div>
                  </label>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors disabled:opacity-50 text-lg font-semibold"
                data-testid="place-order-button"
              >
                {loading ? 'Procesando...' : 'Realizar Pedido'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CheckoutPage;
